import { FC, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { setUser } from 'ducks/user'
import { TOKEN } from 'constants/global.constants'
import { useNotification } from 'lib/context/notification.context'
import Logo from '../../components/UI/Logo'
import { Button } from '../../components/UI/Buttons'
import Input from '../../components/UI/TextInput'
import { H2, P } from '../../components/UI/Text'
import Separator from 'views/components/UI/Separator/Index'
import {
  ActionsRow,
  Container,
  ErrorText,
  ForgotPassword,
  FormBody,
  FormField,
  SecondaryAction,
} from '../../components/Forms/Login/styled'
import verifyMfa from 'services/login/verifyMfa'
import resendMfa from 'services/login/resendMfa'
import {
  IMfaChallengeSession,
  MFA_LOGIN_STORAGE_KEY,
  MFA_TRUSTED_DEVICE_STORAGE_KEY,
} from 'services/login/mfa-session'

const MFA_RESEND_MINIMUM_SECONDS = 60

interface IMfaFormValues {
  code: string
  rememberDevice: boolean
}

const SignInSchema = Yup.object().shape({
  code: Yup.string()
    .matches(/^\d{6}$/, 'El código debe tener 6 dígitos')
    .required('El código MFA es requerido'),
})

const LoginMfaContainer: FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getError, getSuccess, getWarning } = useNotification()

  const [challenge, setChallenge] = useState<IMfaChallengeSession | null>(null)
  const [secondsUntilResend, setSecondsUntilResend] = useState<number>(0)
  const [attemptsLeft, setAttemptsLeft] = useState<number>(5)
  const [loadingVerify, setLoadingVerify] = useState<boolean>(false)
  const [loadingResend, setLoadingResend] = useState<boolean>(false)

  useEffect(() => {
    const storedChallenge = sessionStorage.getItem(MFA_LOGIN_STORAGE_KEY)

    if (!storedChallenge) {
      navigate('/login', { replace: true })
      return
    }

    try {
      const parsedChallenge: IMfaChallengeSession = JSON.parse(storedChallenge) as IMfaChallengeSession
      setChallenge(parsedChallenge)
      setAttemptsLeft(parsedChallenge.attemptsLeft ?? 5)
    } catch {
      sessionStorage.removeItem(MFA_LOGIN_STORAGE_KEY)
      navigate('/login', { replace: true })
    }
  }, [navigate])

  useEffect(() => {
    if (!challenge) {
      return
    }

    const syncTimers = () => {
      const sentAt = new Date(challenge.sentAt).getTime()
      const elapsedSeconds = Math.floor((Date.now() - sentAt) / 1000)
      const resendRemaining = Math.max(0, MFA_RESEND_MINIMUM_SECONDS - elapsedSeconds)

      setSecondsUntilResend(resendRemaining)
    }

    syncTimers()
    const timerId = window.setInterval(syncTimers, 1000)

    return () => {
      window.clearInterval(timerId)
    }
  }, [challenge])

  const codeValidityLabel = challenge
    ? (() => {
        const sentAt = new Date(challenge.sentAt).getTime()
        const remainingSeconds = Math.max(
          0,
          challenge.expiresInMinutes * 60 - Math.floor((Date.now() - sentAt) / 1000),
        )
        const remainingMinutes = Math.floor(remainingSeconds / 60)
        const remainingSecondsPart = remainingSeconds % 60

        return `${remainingMinutes}m ${remainingSecondsPart.toString().padStart(2, '0')}s`
      })()
    : ''

  const persistChallenge = (nextChallenge: IMfaChallengeSession) => {
    setChallenge(nextChallenge)
    setAttemptsLeft(nextChallenge.attemptsLeft)
    sessionStorage.setItem(MFA_LOGIN_STORAGE_KEY, JSON.stringify(nextChallenge))
  }

  const handleSubmit = async (values: IMfaFormValues) => {
    if (!challenge) {
      navigate('/login', { replace: true })
      return
    }

    setLoadingVerify(true)

    try {
      const response = await verifyMfa({
        challengeToken: challenge.challengeToken,
        code: values.code.trim(),
        rememberDevice: values.rememberDevice && challenge.canRememberDevice,
      })
      const user = response.data

      dispatch(
        setUser({
          token: user.token,
          email: challenge.email,
          companyId: user.company_id || challenge.companyId || 0,
          userName: user.user_name || challenge.userName || '',
          hasAdminPlatformManagement: false,
          hasQuestionnaireManagement: false,
        }),
      )

      localStorage.setItem(TOKEN, user.token)
      if (user.mfa_trusted_device_token) {
        localStorage.setItem(
          MFA_TRUSTED_DEVICE_STORAGE_KEY,
          user.mfa_trusted_device_token,
        )
      }
      sessionStorage.removeItem(MFA_LOGIN_STORAGE_KEY)
      navigate('/', { replace: true })
    } catch (error: any) {
      const status = error?.response?.status

      if (status === 401) {
        const nextAttempts = Math.max(0, attemptsLeft - 1)
        setAttemptsLeft(nextAttempts)
        persistChallenge({
          ...challenge,
          attemptsLeft: nextAttempts,
        })
        getWarning('El código MFA es incorrecto')
        return
      }

      if (status === 423) {
        sessionStorage.removeItem(MFA_LOGIN_STORAGE_KEY)
        getWarning('El código MFA expiró. Vuelve a iniciar sesión')
        navigate('/login', { replace: true })
        return
      }

      if (status === 429) {
        sessionStorage.removeItem(MFA_LOGIN_STORAGE_KEY)
        getWarning('Superaste el límite de validaciones permitidas')
        navigate('/login', { replace: true })
        return
      }

      getError('No fue posible validar el código MFA')
    } finally {
      setLoadingVerify(false)
    }
  }

  const handleResend = async () => {
    if (!challenge || secondsUntilResend > 0 || challenge.resendCount >= 3) {
      return
    }

    setLoadingResend(true)

    try {
      const response = await resendMfa({
        challengeToken: challenge.challengeToken,
      })
      const nextChallenge: IMfaChallengeSession = {
        ...challenge,
        sentAt: new Date().toISOString(),
        resendCount: response.data?.resend_count ?? challenge.resendCount + 1,
        attemptsLeft: 5,
      }

      persistChallenge(nextChallenge)
      getSuccess('Código reenviado')
    } catch (error: any) {
      const status = error?.response?.status

      if (status === 423) {
        sessionStorage.removeItem(MFA_LOGIN_STORAGE_KEY)
        getWarning('El código MFA expiró. Vuelve a iniciar sesión')
        navigate('/login', { replace: true })
        return
      }

      if (status === 429) {
        getWarning('Aún no puedes reenviar el código o ya alcanzaste el límite')
        return
      }

      if (status === 401) {
        getWarning('El challenge MFA no es válido')
        return
      }

      getError('No fue posible reenviar el código MFA')
    } finally {
      setLoadingResend(false)
    }
  }

  const handleCancel = () => {
    sessionStorage.removeItem(MFA_LOGIN_STORAGE_KEY)
    navigate('/login', { replace: true })
  }

  if (!challenge) {
    return null
  }

  return (
    <Container>
      <Logo width="245px" />
      <Separator height="1rem" />
      <H2 sx={{ width: 420, margin: '0px 0px 12px 0px', alignSelf: 'center', textAlign: 'center' }}>
        Verifica tu acceso
      </H2>
      <P sx={{ width: '90%', textAlign: 'center', alignSelf: 'center' }}>
        Hemos enviado un código de 6 dígitos a <strong>{challenge.email}</strong>. Introduce el código para continuar.
      </P>
      <Separator height="1.5rem" />
      <Formik<IMfaFormValues>
        validationSchema={SignInSchema}
        onSubmit={handleSubmit}
        initialValues={{ code: '', rememberDevice: false }}
      >
        {({ errors, touched, values, handleChange, isValid }) => (
          <Form style={{ width: '100%' }}>
            <FormBody>
              <FormField>
                <Input
                  value={values.code}
                  onChange={handleChange('code')}
                  type="text"
                  name="code"
                  label="Código MFA"
                  inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                />
                {errors.code && touched.code ? (
                  <div>
                    <ErrorText>{errors.code}</ErrorText>
                  </div>
                ) : null}
              </FormField>
              <P sx={{ fontSize: '13px', margin: 0 }}>
                Intentos restantes: <strong>{attemptsLeft}</strong>
              </P>
              <P sx={{ fontSize: '13px', margin: 0 }}>El código expira en: <strong>{codeValidityLabel}</strong></P>
              {challenge.canRememberDevice ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    name="rememberDevice"
                    checked={values.rememberDevice}
                    onChange={handleChange('rememberDevice')}
                  />
                  Recordar este dispositivo durante 30 dias
                </label>
              ) : null}
              <ActionsRow>
                <SecondaryAction>
                  <ForgotPassword onClick={handleCancel}>Usar otra cuenta</ForgotPassword>
                </SecondaryAction>
                <Button
                  fullWidth
                  disabled={!isValid || loadingVerify}
                  type="submit"
                  size="lg"
                >
                  {loadingVerify ? 'Validando...' : 'Validar código'}
                </Button>
              </ActionsRow>
              <ActionsRow>
                <SecondaryAction>
                  <ForgotPassword
                    onClick={handleResend}
                    style={{
                      pointerEvents: secondsUntilResend > 0 || challenge.resendCount >= 3 || loadingResend ? 'none' : 'auto',
                      opacity: secondsUntilResend > 0 || challenge.resendCount >= 3 || loadingResend ? 0.5 : 1,
                    }}
                  >
                    {loadingResend
                      ? 'Reenviando...'
                      : secondsUntilResend > 0
                        ? `Reenviar en ${secondsUntilResend}s`
                        : challenge.resendCount >= 3
                          ? 'Límite de reenvíos alcanzado'
                          : 'Reenviar código'}
                  </ForgotPassword>
                </SecondaryAction>
              </ActionsRow>
            </FormBody>
          </Form>
        )}
      </Formik>
    </Container>
  )
}

export default LoginMfaContainer
